// frontend/src/components/reputation/OfficeVisibilityBlock.jsx
// Version: 1.1 (Phase 1 C2 Geogrid Visibility Integration - Cleaned & Truthful)
// Description: Отображение локальной видимости офиса в Google Maps (геосетка 7x7).
//
// 🔴 ПРАВИЛО ЧЕСТНОСТИ ДАННЫХ:
// 1. Если замер не проводился (status === "not_measured"), показывается прочерк «—».
//    ПОДСТАНОВКА 0 или 0% ЗАПРЕЩЕНА: «ноль» означает «замерили и не нашли»,
//    а прочерк означает «измерения не было».
// 2. Все метрики и точки сетки берутся СТРОГО из ответа бэкенда (полученного от C2).
// 3. Блок «Что эти числа не доказывают» обязателен, чтобы исключить ложные ожидания.
// 4. Провайдер, даты и охват берутся ИСКЛЮЧИТЕЛЬНО из данных замера, без подстановок.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Grid,
  MapPin,
  TrendingUp,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Award,
  Users,
  Sliders,
  Search
} from 'lucide-react';
import reputationService from '../../services/reputationService';
import { fetchGeogridData, updateGeogridSettingsInCache } from '../../services/geogridCache';
import { useUiLanguage, perevesti } from '../../context/UiLanguageContext';
import { useBranding } from '../../context/BrandingContext';
import GeogridKeywordsModal from './GeogridKeywordsModal';

/**
 * Краткое форматирование даты замера для карточки (напр. "13 окт.")
 */
export const formatShortEdtDate = (isoString, lang = 'ru') => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '—';

  const ruMonths = [
    'янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июн.',
    'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'
  ];
  const enMonths = [
    'Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.',
    'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'
  ];
  const months = lang === 'en' ? enMonths : ruMonths;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(d);

    const getP = (t) => parts.find((p) => p.type === t)?.value || '';
    const mNum = parseInt(getP('month'), 10);
    const mStr = (mNum >= 1 && mNum <= 12) ? months[mNum - 1] : getP('month');

    if (lang === 'en') {
      return `${mStr} ${getP('day')}`;
    }
    return `${getP('day')} ${mStr}`;
  } catch (err) {
    return isoString.slice(5, 10);
  }
};

/**
 * Форматирование момента времени в пояс Америки/Нью-Йорк (EDT/EST):
 * Образец: "13 окт. 2026, 23:24 EDT"
 */
export const formatEdtDateTime = (isoString, lang = 'ru') => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '—';

  const ruMonths = [
    'янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июн.',
    'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'
  ];
  const enMonths = [
    'Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.',
    'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'
  ];
  const months = lang === 'en' ? enMonths : ruMonths;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      hour12: false,
    }).formatToParts(d);

    const getP = (t) => parts.find((p) => p.type === t)?.value || '';
    const mNum = parseInt(getP('month'), 10);
    const mStr = (mNum >= 1 && mNum <= 12) ? months[mNum - 1] : getP('month');
    const tzName = getP('timeZoneName') || 'EDT';

    if (lang === 'en') {
      return `${mStr} ${getP('day')}, ${getP('year')}, ${getP('hour')}:${getP('minute')} ${tzName}`;
    }
    return `${getP('day')} ${mStr} ${getP('year')}, ${getP('hour')}:${getP('minute')} ${tzName}`;
  } catch (err) {
    return isoString;
  }
};

export const OfficeVisibilityBlock = ({
  kluch,
  knownOfficePlaceIds = [],
  className = '',
  officeTitle = null,
  actionButtons = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [selectedKwName, setSelectedKwName] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [isKeywordsModalOpen, setIsKeywordsModalOpen] = useState(false);

  const { lang: rawLang, t: contextT } = useUiLanguage();
  const { branding } = useBranding();
  const lang = rawLang || 'ru';
  const isEn = lang === 'en';
  const t = useCallback((k, v) => (rawLang ? contextT(k, v) : perevesti('ru', k, v)), [rawLang, contextT]);

  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);

  // Закрытие выпадающего списка при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Загрузка данных замера и настроек офиса через единый кэш
  useEffect(() => {
    let isMounted = true;
    if (!kluch) return;

    setLoading(true);
    setError(null);

    fetchGeogridData(kluch)
      .then(({ geogrid, settings }) => {
        if (!isMounted) return;
        setData(geogrid);
        if (settings) {
          setSettingsData(settings);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.warn(`[OfficeVisibilityBlock] Error fetching geogrid for ${kluch}:`, err);
        setError(err?.message || (isEn ? 'Failed to load scan data' : 'Ошибка загрузки данных замера'));
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [kluch]);

  const payload = data?.payload;
  const isMeasured = Boolean(data?.status === 'measured' && payload?.run);

  // 1. Замеренные запросы: строго в порядке run.keywords (без пересортировки)
  const measuredKeywords = useMemo(() => {
    if (!isMeasured || !Array.isArray(payload?.run?.keywords)) return [];
    return payload.run.keywords;
  }, [isMeasured, payload?.run?.keywords]);

  // 2. Запросы из настроек, которые ещё не замерялись (появятся после следующего замера)
  const unmeasuredKeywords = useMemo(() => {
    const configured = settingsData?.keywords || settingsData?.config_keywords;
    if (!Array.isArray(configured) || configured.length === 0) return [];
    const measuredSet = new Set(measuredKeywords.map((k) => k.keyword?.toLowerCase().trim()));
    return configured
      .filter((k) => typeof k === 'string' && k.trim() && !measuredSet.has(k.toLowerCase().trim()))
      .map((k) => ({
        keyword: k.trim(),
        metrics: null,
        isUnmeasured: true,
      }));
  }, [settingsData, measuredKeywords]);

  const allKeywords = useMemo(() => {
    return [...measuredKeywords, ...unmeasuredKeywords];
  }, [measuredKeywords, unmeasuredKeywords]);

  // Синхронизация и выбор по умолчанию с сохранением в localStorage
  useEffect(() => {
    if (!allKeywords.length) {
      setSelectedKwName('');
      return;
    }

    let saved = null;
    try {
      saved = localStorage.getItem(`fitenko_geogrid_selected_kw_${kluch}`);
    } catch (e) {
      // ignore
    }

    if (saved && allKeywords.some((k) => k.keyword === saved)) {
      setSelectedKwName(saved);
    } else {
      const fallback = measuredKeywords[0]?.keyword || unmeasuredKeywords[0]?.keyword || '';
      setSelectedKwName(fallback);
      if (saved) {
        try {
          localStorage.removeItem(`fitenko_geogrid_selected_kw_${kluch}`);
        } catch (e) {
          // ignore
        }
      }
    }
  }, [kluch, measuredKeywords, unmeasuredKeywords, allKeywords]);

  const handleSelectKeyword = (kwName) => {
    setSelectedKwName(kwName);
    setIsDropdownOpen(false);
    try {
      localStorage.setItem(`fitenko_geogrid_selected_kw_${kluch}`, kwName);
    } catch (e) {
      // ignore
    }
  };

  const term = searchTerm.trim().toLowerCase();
  const filteredMeasured = useMemo(() => {
    return term ? measuredKeywords.filter((k) => k.keyword.toLowerCase().includes(term)) : measuredKeywords;
  }, [measuredKeywords, term]);

  const filteredUnmeasured = useMemo(() => {
    return term ? unmeasuredKeywords.filter((k) => k.keyword.toLowerCase().includes(term)) : unmeasuredKeywords;
  }, [unmeasuredKeywords, term]);

  const allVisibleOptions = useMemo(() => {
    return [...filteredMeasured, ...filteredUnmeasured];
  }, [filteredMeasured, filteredUnmeasured]);

  const handleOptionKeyDown = (e, index, kw) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (index + 1) % allVisibleOptions.length;
      optionRefs.current[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (index - 1 + allVisibleOptions.length) % allVisibleOptions.length;
      optionRefs.current[prevIndex]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      optionRefs.current[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      optionRefs.current[allVisibleOptions.length - 1]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelectKeyword(kw.keyword);
      triggerRef.current?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsDropdownOpen(false);
      triggerRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsDropdownOpen(true);
      if (allKeywords.length > 8) {
        setTimeout(() => searchInputRef.current?.focus(), 50);
      } else {
        setTimeout(() => optionRefs.current[0]?.focus(), 50);
      }
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  const activeKw = useMemo(() => {
    const found = allKeywords.find((k) => k.keyword === selectedKwName);
    return found || measuredKeywords[0] || unmeasuredKeywords[0] || null;
  }, [allKeywords, selectedKwName, measuredKeywords, unmeasuredKeywords]);

  const isKwUnmeasured = Boolean(activeKw?.isUnmeasured || !activeKw?.metrics);

  // Фильтрация наблюдений для активного ключевого слова
  const observationsForKw = useMemo(() => {
    if (isKwUnmeasured || !isMeasured || !Array.isArray(payload?.observations) || !activeKw?.keyword) return [];
    return payload.observations.filter((obs) => obs.keyword === activeKw.keyword);
  }, [isKwUnmeasured, isMeasured, payload?.observations, activeKw?.keyword]);

  // Формирование матрицы сетки (по умолчанию 7x7)
  const gridMatrix = useMemo(() => {
    const rows = payload?.run?.grid?.rows || 7;
    const cols = payload?.run?.grid?.cols || 7;
    const matrix = Array.from({ length: rows }, () => Array(cols).fill(null));

    if (!isKwUnmeasured && Array.isArray(observationsForKw)) {
      observationsForKw.forEach((obs) => {
        if (
          obs &&
          typeof obs.row === 'number' &&
          typeof obs.col === 'number' &&
          obs.row >= 0 &&
          obs.row < rows &&
          obs.col >= 0 &&
          obs.col < cols
        ) {
          matrix[obs.row][obs.col] = obs;
        }
      });
    }

    return { rows, cols, matrix };
  }, [isKwUnmeasured, observationsForKw, payload?.run?.grid?.rows, payload?.run?.grid?.cols]);

  // Счётчики по наблюдениям выбранного запроса (если наблюдения загружены)
  const obsTop3Count = useMemo(() => {
    if (isKwUnmeasured || !Array.isArray(observationsForKw) || !observationsForKw.length) return null;
    return observationsForKw.filter(
      (obs) => typeof obs?.rank === 'number' && obs.rank > 0 && obs.rank <= 3
    ).length;
  }, [isKwUnmeasured, observationsForKw]);

  const obsTop10Count = useMemo(() => {
    if (isKwUnmeasured || !Array.isArray(observationsForKw) || !observationsForKw.length) return null;
    return observationsForKw.filter(
      (obs) => typeof obs?.rank === 'number' && obs.rank > 0 && obs.rank <= 10
    ).length;
  }, [isKwUnmeasured, observationsForKw]);

  const obsFoundCount = useMemo(() => {
    if (isKwUnmeasured || !Array.isArray(observationsForKw) || !observationsForKw.length) return null;
    const depth = payload?.run?.depth || 20;
    return observationsForKw.filter(
      (obs) => typeof obs?.rank === 'number' && obs.rank > 0 && obs.rank <= depth
    ).length;
  }, [isKwUnmeasured, observationsForKw, payload?.run?.depth]);

  // Дельта к прошлому замеру (источник строго серверный: C2 / backend deltas)
  const activeDelta = useMemo(() => {
    if (isKwUnmeasured || !activeKw?.keyword) return null;
    return data?.deltas?.[activeKw.keyword] || null;
  }, [isKwUnmeasured, activeKw?.keyword, data?.deltas]);

  // Точка сетки, математически ближайшая к фактическим координатам офиса
  const nearestOfficeCell = useMemo(() => {
    const defaultR = Math.floor((payload?.run?.grid?.rows || 7) / 2);
    const defaultC = Math.floor((payload?.run?.grid?.cols || 7) / 2);

    if (!payload?.run) {
      return { r: defaultR, c: defaultC };
    }

    const officeLat = payload?.run?.office?.lat ?? payload?.office?.lat;
    const officeLng = payload?.run?.office?.lng ?? payload?.office?.lng;

    if (typeof officeLat !== 'number' || typeof officeLng !== 'number') {
      return { r: defaultR, c: defaultC };
    }

    let minDistanceSq = Infinity;
    let nearest = { r: defaultR, c: defaultC };

    if (gridMatrix?.matrix) {
      gridMatrix.matrix.forEach((rowArr, rIdx) => {
        rowArr.forEach((cell, cIdx) => {
          if (cell && typeof cell.lat === 'number' && typeof cell.lng === 'number') {
            const dLat = cell.lat - officeLat;
            const dLng = (cell.lng - officeLng) * Math.cos((officeLat * Math.PI) / 180);
            const distSq = dLat * dLat + dLng * dLng;
            if (distSq < minDistanceSq) {
              minDistanceSq = distSq;
              nearest = { r: rIdx, c: cIdx };
            }
          }
        });
      });
    }

    return nearest;
  }, [
    payload?.run?.office?.lat,
    payload?.run?.office?.lng,
    payload?.office?.lat,
    payload?.office?.lng,
    payload?.run?.grid?.rows,
    payload?.run?.grid?.cols,
    gridMatrix
  ]);

  const ownPlaceId = payload?.target?.placeId || payload?.run?.target?.placeId;
  const knownPlaceIdsSet = useMemo(() => new Set(knownOfficePlaceIds), [knownOfficePlaceIds]);

  // Извлечение топ-конкурентов по активному запросу (исключая текущий офис, но с пометкой других своих офисов)
  const topCompetitors = useMemo(() => {
    if (!Array.isArray(observationsForKw) || !observationsForKw.length) return [];
    const compMap = new Map();

    observationsForKw.forEach((obs) => {
      if (Array.isArray(obs?.topEntries)) {
        obs.topEntries.forEach((entry) => {
          if (!entry || !entry.title) return;
          const entryPlaceId = entry.placeId;

          // Текущий офис замера исключаем (он показан отдельной строкой выше)
          if (ownPlaceId && entryPlaceId && entryPlaceId === ownPlaceId) {
            return;
          }

          // Другие офисы нашей фирмы (из канона держателя) помечаем, не скрывая
          const isOtherOwnOffice = Boolean(entryPlaceId && knownPlaceIdsSet.has(entryPlaceId));

          const key = entryPlaceId || entry.title;
          const existing = compMap.get(key) || {
            title: entry.title,
            placeId: entryPlaceId,
            rating: entry.rating,
            reviewsCount: entry.reviewsCount,
            isOtherOwnOffice,
            top3Count: 0,
            ranks: []
          };
          if (typeof entry.rank === 'number' && entry.rank <= 3) existing.top3Count += 1;
          existing.ranks.push(entry.rank);
          compMap.set(key, existing);
        });
      }
    });

    return Array.from(compMap.values())
      .filter((c) => c.top3Count > 0)
      .sort((a, b) => b.top3Count - a.top3Count)
      .slice(0, 3);
  }, [observationsForKw, ownPlaceId, knownPlaceIdsSet]);



  const handleKeywordsSaved = (savedKeywords) => {
    setSettingsData((prev) => ({
      ...(prev || {}),
      keywords: savedKeywords,
      source: 'OS',
    }));
    if (kluch && typeof reputationService.getGeogridSettings === 'function') {
      reputationService
        .getGeogridSettings(kluch)
        .then((res) => {
          if (res) setSettingsData(res);
        })
        .catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className={`p-3.5 rounded-lg bg-[#FAF9F5] border border-[#E5E1D6] animate-pulse ${className}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="h-3.5 bg-[#E5E1D6] rounded w-1/3" />
          <div className="h-3.5 bg-[#E5E1D6] rounded w-16" />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="h-10 bg-[#E5E1D6] rounded" />
          <div className="h-10 bg-[#E5E1D6] rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-3 rounded-lg bg-[#FFF5F5] border border-[#FED7D7] text-xs text-[#C53030] ${className}`}>
        <div className="flex items-center gap-1.5 font-medium">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{t('rep.geogrid_ui.service')}</span>
        </div>
        <div className="mt-1 text-[11px] text-[#742A2A]">{error}</div>
      </div>
    );
  }

  const abortStatus = data?.abort_status;
  const c2Reputation = data?.c2_reputation;

  // 1. Состояние: НЕ ИЗМЕРЯЛОСЬ (прочерк «—», нули запрещены)
  if (!isMeasured) {
    return (
      <div className={`p-4 sm:p-5 rounded-xl bg-[#FAF9F5] border border-[#E5E1D6] text-xs ${className}`}>
        {officeTitle && (
          <div className="pb-3 mb-3 border-b border-[#E5E1D6] flex items-center justify-between">
            <h4 className="text-sm sm:text-base font-bold text-[#20231F]">{officeTitle}</h4>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-1.5 font-semibold text-[#20231F]">
            <Grid className="w-3.5 h-3.5 text-[#C8A653]" />
            <span>{t('rep.geogrid_ui.title')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setIsKeywordsModalOpen(true)}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded border border-[#E5E1D6] bg-white text-[#20231F] hover:bg-[#F1EEE4] hover:border-[#C8A653] transition-colors"
              title={t('rep.geogrid_ui.configure_queries_title')}
            >
              <Sliders className="w-3 h-3 text-[#8C7A5B]" />
              <span>{t('rep.geogrid_ui.configure_queries')}</span>
            </button>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-[#F1EEE4] text-[#687077]">
              {t('rep.geogrid_ui.not_measured')}
            </span>
          </div>
        </div>

        {abortStatus && (
          <div className="mt-2.5 p-2.5 rounded-lg bg-[#FFF5F5] border border-[#FED7D7] text-[#C53030] text-[11px] flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-[#E53E3E]" />
            <div className="flex-1">
              <div className="font-bold">
                {isEn ? `Scan not executed: ${abortStatus.reason || 'query list exceeds budget'}` : `Замер не выполнен: ${abortStatus.reason || 'список больше бюджета'}`}
              </div>
              <div className="text-[10px] text-[#742A2A] mt-0.5">
                {isEn
                  ? `Planned ${abortStatus.planned_requests || '—'} requests with budget limit ${abortStatus.budget_limit || '250'}. Open "Configure queries" to shorten list.`
                  : `Запланировано ${abortStatus.planned_requests || '—'} обращений при лимите ${abortStatus.budget_limit || '250'}. Откройте «Настроить запросы» и сократите список.`}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mt-2.5">
          <div className="p-2 rounded bg-white border border-[#E5E1D6]">
            <div className="text-[10px] text-[#687077] uppercase font-bold tracking-wider">
              {t('rep.geogrid_ui.top3_share')}
            </div>
            <div className="text-base font-bold text-[#687077] mt-0.5">—</div>
          </div>
          <div className="p-2 rounded bg-white border border-[#E5E1D6]">
            <div className="text-[10px] text-[#687077] uppercase font-bold tracking-wider">
              {t('rep.geogrid_ui.top10_share')}
            </div>
            <div className="text-base font-bold text-[#687077] mt-0.5">—</div>
          </div>
        </div>

        <div className="text-[10px] text-[#687077] mt-2 leading-relaxed">
          {t('rep.geogrid_ui.scan_never_run')}
        </div>

        {actionButtons && (
          <div className="mt-4 pt-3 border-t border-[#E5E1D6] flex flex-wrap items-center gap-2">
            {actionButtons}
          </div>
        )}

        <GeogridKeywordsModal
          isOpen={isKeywordsModalOpen}
          onClose={() => setIsKeywordsModalOpen(false)}
          onSaved={handleKeywordsSaved}
          kluch={kluch}
          officeName={kluch}
        />
      </div>
    );
  }

  // 2. Состояние: БОЕВОЙ ЗАМЕР ПРИНЯТ
  const metrics = isKwUnmeasured ? null : activeKw?.metrics;
  const totalRequested = (typeof metrics?.totalRequested === 'number')
    ? metrics.totalRequested
    : ((typeof gridMatrix.rows === 'number' && typeof gridMatrix.cols === 'number')
        ? gridMatrix.rows * gridMatrix.cols
        : null);

  const top3Pct =
    (!isKwUnmeasured && typeof metrics?.unweightedTop3Share === 'number')
      ? `${(metrics.unweightedTop3Share * 100).toFixed(1)}%`
      : '—';
  const top10Pct =
    (!isKwUnmeasured && typeof metrics?.unweightedTop10Share === 'number')
      ? `${(metrics.unweightedTop10Share * 100).toFixed(1)}%`
      : '—';

  const top3Count =
    !isKwUnmeasured
      ? (obsTop3Count !== null
          ? obsTop3Count
          : (typeof metrics?.unweightedTop3Share === 'number' && typeof totalRequested === 'number'
              ? Math.round(metrics.unweightedTop3Share * totalRequested)
              : null))
      : null;
  const top10Count =
    !isKwUnmeasured
      ? (obsTop10Count !== null
          ? obsTop10Count
          : (typeof metrics?.unweightedTop10Share === 'number' && typeof totalRequested === 'number'
              ? Math.round(metrics.unweightedTop10Share * totalRequested)
              : null))
      : null;
  const foundCount =
    !isKwUnmeasured
      ? (obsFoundCount !== null
          ? obsFoundCount
          : (typeof metrics?.foundCount === 'number' ? metrics.foundCount : null))
      : null;

  const rawDate =
    payload?.run?.finishedAt ||
    payload?.run?.completedAt ||
    payload?.run?.startedAt ||
    data?.received_at;
  const dateFormatted = formatEdtDateTime(rawDate, lang);

  // Параметры геосетки (Пункт 6: шаг ~1.7 км, размер 10×10 км, С ↑)
  const radiusKm = typeof payload?.run?.grid?.radiusKm === 'number'
    ? payload.run.grid.radiusKm
    : null;
  const gridRows = (typeof gridMatrix.rows === 'number' && gridMatrix.rows > 0)
    ? gridMatrix.rows
    : (typeof payload?.run?.grid?.rows === 'number' ? payload.run.grid.rows : null);
  const gridCols = (typeof gridMatrix.cols === 'number' && gridMatrix.cols > 0)
    ? gridMatrix.cols
    : (typeof payload?.run?.grid?.cols === 'number' ? payload.run.grid.cols : null);

  const sizeKm = radiusKm !== null ? Math.round(radiusKm * 2) : null;
  const stepKm = (radiusKm !== null && gridRows !== null && gridRows > 1)
    ? ((2 * radiusKm) / (gridRows - 1)).toFixed(1)
    : null;

  // Охват вычисляется динамически из данных замера (никогда не вбивается строкой)
  const coveragePercent = (!isKwUnmeasured && typeof metrics?.measurementCoverage === 'number')
    ? Math.round(metrics.measurementCoverage * 100)
    : (!isKwUnmeasured && typeof metrics?.totalRequested === 'number' && typeof metrics?.validObservationsCount === 'number' && metrics.totalRequested > 0
        ? Math.round((metrics.validObservationsCount / metrics.totalRequested) * 100)
        : null);

  return (
    <div className={`p-4 sm:p-5 rounded-xl bg-[#FAF9F5] border border-[#E5E1D6] text-xs space-y-4 ${className}`}>
      {officeTitle && (
        <div className="pb-3 border-b border-[#E5E1D6] flex items-center justify-between">
          <h4 className="text-sm sm:text-base font-bold text-[#20231F]">{officeTitle}</h4>
        </div>
      )}

      {/* Шапка замера (Пункт 7: штамп времени EDT/EST) */}
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 font-semibold text-[#20231F]">
          <Grid className="w-3.5 h-3.5 text-[#C8A653]" />
          <span>{t('rep.geogrid_ui.scan_heading')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]"
            title={t('rep.geogrid_ui.scan_title', { date: dateFormatted })}
          >
            {t('rep.geogrid_ui.scan_badge', { date: dateFormatted })}
          </span>
        </div>
      </div>

      {/* Подтверждённый рейтинг карточки из Google Maps через замер C2 */}
      {c2Reputation && c2Reputation.status === 'measured' && c2Reputation.rating !== null ? (
        <div
          data-testid="c2-reputation-pill"
          className="p-2.5 rounded-lg bg-white border border-[#E5E1D6] flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 shadow-2xs"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#A97F2E]">★ {c2Reputation.rating.toFixed(1)}</span>
            <span className="text-xs font-semibold text-[#20231F]">
              {c2Reputation.reviews_count !== null ? (isEn ? `${c2Reputation.reviews_count} reviews` : `${c2Reputation.reviews_count} отзывов`) : (isEn ? 'reviews not specified' : 'отзывы не указаны')}
            </span>
            <span className="text-[11px] text-[#687077]">{isEn ? '· Google Maps via C2 scan' : '· Google Maps через замер C2'}</span>
          </div>
          <div className="text-[10px] text-[#687077] flex items-center gap-1.5" title={c2Reputation.signature}>
            <span>{c2Reputation.scan_time_edt}</span>
            <span>({isEn ? `across ${c2Reputation.points_found} points where card was found` : (c2Reputation.points_found_text || `по ${c2Reputation.points_found} точкам, где карточка найдена`)})</span>
          </div>
        </div>
      ) : (
        <div data-testid="c2-reputation-pill" className="p-2 rounded-lg bg-[#FAF9F5] border border-[#E5E1D6] text-[11px] text-[#687077] flex items-center justify-between">
          <span>{t('rep.geogrid_ui.rating_unmeasured')}</span>
          <span className="text-[10px] text-[#8C7A5B]">{t('rep.geogrid_ui.will_measure_next')}</span>
        </div>
      )}

      {c2Reputation?.has_divergence && (
        <div data-testid="c2-reputation-divergence" className="p-2 rounded-lg bg-[#FFFBEB] border border-[#FDE68A] text-[10px] text-[#92400E] flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#D97706]" />
          <span>{c2Reputation.divergence_notes}</span>
        </div>
      )}

      {abortStatus && (
        <div className="p-2.5 rounded-lg bg-[#FFF5F5] border border-[#FED7D7] text-[#C53030] text-[11px] flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-[#E53E3E]" />
          <div className="flex-1">
            <div className="font-bold">
              {isEn ? `Scan not executed: ${abortStatus.reason || 'query list exceeds budget'}` : `Замер не выполнен: ${abortStatus.reason || 'список больше бюджета'}`}
            </div>
            <div className="text-[10px] text-[#742A2A] mt-0.5">
              {isEn
                ? `Planned ${abortStatus.planned_requests || '—'} requests with budget limit ${abortStatus.budget_limit || '250'}. Showing previous scan data. Open "Configure queries" to shorten list.`
                : `Запланировано ${abortStatus.planned_requests || '—'} обращений при лимите ${abortStatus.budget_limit || '250'}. Показаны данные предыдущего замера. Откройте «Настроить запросы» и сократите список.`}
            </div>
          </div>
        </div>
      )}

      {/* Выпадающий список запросов и кнопка «Настроить запросы» */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
        <div className="relative flex-1 min-w-[200px]" ref={dropdownRef}>
          <button
            type="button"
            ref={triggerRef}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            onKeyDown={handleKeyDown}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            aria-label={t('rep.geogrid_ui.query_label')}
            title={activeKw?.keyword || t('rep.geogrid_ui.choose_query')}
            className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-[#E5E1D6] bg-white text-[#20231F] hover:bg-[#FAF9F5] hover:border-[#C8A653] transition-colors shadow-2xs text-left"
          >
            <span className="flex-1 min-w-0 truncate">
              <span className="text-[#687077] font-normal mr-1">{t('rep.geogrid_ui.query_label')}</span>
              <span className="font-semibold text-[#20231F]">{activeKw?.keyword || '—'}</span>
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-[#687077] shrink-0 transition-transform ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Выпадающее меню */}
          <div
            role="listbox"
            aria-label={t('rep.geogrid_ui.choose_query')}
            className={`${
              isDropdownOpen ? 'block' : 'hidden'
            } absolute left-0 z-30 mt-1 w-full min-w-[280px] max-w-[420px] bg-white border border-[#E5E1D6] rounded-md shadow-lg overflow-hidden`}
          >
            {/* Поиск по тексту, если запросов больше 8 */}
            {allKeywords.length > 8 && (
              <div className="p-2 border-b border-[#F1EEE4] bg-[#FAF9F5]">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#9CA3AF]" />
                  <input
                    type="text"
                    ref={searchInputRef}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('rep.geogrid_ui.search_placeholder')}
                    aria-label={t('rep.geogrid_ui.search_placeholder')}
                    className="w-full pl-7 pr-2 py-1 text-xs border border-[#E5E1D6] rounded bg-white focus:outline-none focus:border-[#C8A653]"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setIsDropdownOpen(false);
                        triggerRef.current?.focus();
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        optionRefs.current[0]?.focus();
                      }
                    }}
                  />
                </div>
              </div>
            )}

            <div className="max-h-60 overflow-y-auto py-1">
              {/* Группа 1: Замеренные запросы */}
              {filteredMeasured.length > 0 && (
                <div>
                  {filteredMeasured.map((kw, mIdx) => {
                    const globalIdx = mIdx;
                    const isSelected = kw.keyword === activeKw?.keyword;
                    const top3Share = kw.metrics?.unweightedTop3Share;
                    const shareText =
                      typeof top3Share === 'number'
                        ? `${(top3Share * 100).toFixed(1)}%`
                        : '—';

                    return (
                      <button
                        key={kw.keyword}
                        ref={(el) => { optionRefs.current[globalIdx] = el; }}
                        tabIndex={-1}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelectKeyword(kw.keyword)}
                        onKeyDown={(e) => handleOptionKeyDown(e, globalIdx, kw)}
                        title={kw.keyword}
                        className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left transition-colors focus:outline-none focus:bg-[#E5E1D6] ${
                          isSelected
                            ? 'bg-[#F1EEE4] font-semibold text-[#20231F]'
                            : 'text-[#20231F] hover:bg-[#FAF9F5]'
                        }`}
                      >
                        <span className="truncate mr-2">{kw.keyword}</span>
                        <span
                          className={`shrink-0 font-medium text-[11px] ${
                            shareText !== '—' ? 'text-[#0B8B5E]' : 'text-[#687077]'
                          }`}
                        >
                          {shareText}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Группа 2: Ещё не замерялись */}
              {filteredUnmeasured.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-[10px] uppercase font-bold text-[#8C7A5B] bg-[#FAF9F5] border-t border-b border-[#F1EEE4] tracking-wider">
                    {t('rep.geogrid_ui.not_measured_yet_group')}
                  </div>
                  {filteredUnmeasured.map((kw, uIdx) => {
                    const globalIdx = filteredMeasured.length + uIdx;
                    const isSelected = kw.keyword === activeKw?.keyword;
                    return (
                      <button
                        key={kw.keyword}
                        ref={(el) => { optionRefs.current[globalIdx] = el; }}
                        tabIndex={-1}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelectKeyword(kw.keyword)}
                        onKeyDown={(e) => handleOptionKeyDown(e, globalIdx, kw)}
                        title={kw.keyword}
                        className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left transition-colors focus:outline-none focus:bg-[#E5E1D6] ${
                          isSelected
                            ? 'bg-[#F1EEE4] font-semibold text-[#20231F]'
                            : 'text-[#687077] hover:bg-[#FAF9F5]'
                        }`}
                      >
                        <span className="truncate mr-2">{kw.keyword}</span>
                        <span className="shrink-0 font-medium text-[11px] text-[#687077]">—</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {filteredMeasured.length === 0 && filteredUnmeasured.length === 0 && (
                <div className="px-3 py-3 text-xs text-center text-[#687077]">
                  {t('rep.geogrid_ui.nothing_found')}
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsKeywordsModalOpen(true)}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-[#E5E1D6] bg-white text-[#20231F] hover:bg-[#F1EEE4] hover:border-[#C8A653] transition-colors shrink-0 shadow-2xs"
          title={t('rep.geogrid_ui.configure_queries_title')}
        >
          <Sliders className="w-3.5 h-3.5 text-[#8C7A5B]" />
          <span>{t('rep.geogrid_ui.configure_queries')}</span>
        </button>
      </div>

      {isKwUnmeasured && (
        <div className="p-2 rounded bg-[#FAF9F5] border border-[#E5E1D6] text-[11px] text-[#687077] flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 text-[#C8A653] shrink-0" />
          <span>{t('rep.geogrid_ui.query_unmeasured_notice')}</span>
        </div>
      )}

      {/* ДВЕ КОЛОНКИ НА ШИРОКИХ ЭКРАНАХ (>= 900px, lg) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* ЛЕВАЯ КОЛОНКА: Сетка 7x7 и легенда */}
        <div className="lg:col-span-6 p-3 sm:p-4 rounded-xl bg-white border border-[#E5E1D6] space-y-2.5">
          <div className="flex items-center justify-between text-[11px] text-[#687077]">
            <span
              className="font-semibold text-[#20231F]"
              title={isEn ? `North is up (N ↑) · size: ${sizeKm}×${sizeKm} km · grid step: ~${stepKm} km` : `Север сверху (С ↑) · размер: ${sizeKm}×${sizeKm} км · шаг сетки: ~${stepKm} км`}
            >
              {t('rep.geogrid_ui.grid_title', { rows: gridRows, cols: gridCols, size: sizeKm, step: stepKm })}
            </span>
            <span className="truncate max-w-[140px] text-right font-medium" title={activeKw?.keyword}>
              {activeKw?.keyword}
            </span>
          </div>

          <div
            className="grid gap-1.5 mx-auto w-full max-w-[320px] sm:max-w-[340px]"
            style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}
          >
            {gridMatrix.matrix.map((rowArr, rIdx) =>
              rowArr.map((cell, cIdx) => {
                const depth = payload?.run?.depth || 20;
                const isOfficePoint =
                  rIdx === nearestOfficeCell.r && cIdx === nearestOfficeCell.c;
                const rank = cell?.rank;
                const status = cell?.status;
                const hasRank = typeof rank === 'number' && rank > 0;
                const isNotFoundBeyondDepth =
                  status === 'NOT_FOUND_BEYOND_DEPTH' || (hasRank && rank > depth);

                let cellBg = 'bg-[#F8F7F2] text-[#9CA3AF] border border-[#E5E1D6]';
                let cellText = '—';
                let cellTitle = isEn ? `Point [${rIdx + 1}, ${cIdx + 1}]: not measured / no data` : `Точка [${rIdx + 1}, ${cIdx + 1}]: не измерено / нет данных`;

                if (hasRank && rank <= depth) {
                  cellText = String(rank);
                  cellTitle = isEn ? `Point [${rIdx + 1}, ${cIdx + 1}]: rank #${rank}` : `Точка [${rIdx + 1}, ${cIdx + 1}]: ранг #${rank}`;
                  if (rank <= 3) {
                    cellBg = 'bg-[#0B8B5E] text-white font-bold shadow-2xs';
                  } else if (rank <= 10) {
                    cellBg = 'bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5] font-semibold';
                  } else {
                    cellBg = 'bg-[#F1EEE4] text-[#687077]';
                  }
                } else if (isNotFoundBeyondDepth) {
                  cellText = `>${depth}`;
                  cellBg = 'bg-[#F1EEE4] text-[#687077] border border-[#E5E1D6]';
                  cellTitle = isEn ? `Point [${rIdx + 1}, ${cIdx + 1}]: not in top-${depth} (>${depth})` : `Точка [${rIdx + 1}, ${cIdx + 1}]: не в топ-${depth} (>${depth}`;
                }

                return (
                  <div
                    key={`${rIdx}-${cIdx}`}
                    className={`aspect-square min-w-[34px] min-h-[34px] sm:min-w-[40px] sm:min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-md relative transition-all ${cellBg}`}
                    title={`${cellTitle}${isOfficePoint ? (isEn ? ' (nearest point to office)' : ' (ближайшая к офису точка)') : ''}`}
                  >
                    <span>{cellText}</span>
                    {isOfficePoint && (
                      <span
                        className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#C8A653] ring-1.5 ring-white"
                        title={isEn ? 'Nearest point to office' : 'Ближайшая к офису точка'}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Легенда сетки с подписью С ↑ */}
          <div className="flex flex-wrap items-center justify-between gap-1 text-[10px] text-[#687077] pt-2 border-t border-[#F1EEE4]">
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-xs bg-[#0B8B5E] inline-block" />
              <span>1–3</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-xs bg-[#FAF4E5] border border-[#E5D7B5] inline-block" />
              <span>4–10</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-xs bg-[#F1EEE4] inline-block" />
              <span>11–{payload?.run?.depth || 20}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-xs bg-[#F1EEE4] border border-[#E5E1D6] inline-block" />
              <span>&gt;{payload?.run?.depth || 20}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-xs bg-[#F8F7F2] border border-[#E5E1D6] inline-block" />
              <span>—</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-[#C8A653] inline-block" />
              <span>{t('rep.geogrid_ui.office_pin')}</span>
            </div>
            <div className="flex items-center gap-0.5 font-bold text-[#20231F]" title={t('rep.geogrid_ui.north_up_title')}>
              <span>{t('rep.geogrid_ui.north_up')}</span>
            </div>
          </div>
        </div>

        {/* ПРАВАЯ КОЛОНКА: Сводные показатели, Покрытие, Лидеры */}
        <div className="lg:col-span-6 space-y-3">
          {/* Сводные показатели (Пункт 8: русские подписи Доля в топ-3, Доля в топ-10) */}
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 rounded bg-white border border-[#E5E1D6]">
              <div className="text-[10px] text-[#687077] uppercase font-bold tracking-wider">
                {t('rep.geogrid_ui.top3_share')}
              </div>
              <div className="text-base font-bold text-[#0B8B5E] mt-0.5">{top3Pct}</div>
              {activeDelta?.text && (
                <div
                  data-testid="delta-badge"
                  className={`text-[9px] font-medium mt-0.5 ${
                    activeDelta.status === 'comparable'
                      ? (activeDelta.diff_pp > 0
                          ? 'text-[#0B8B5E]'
                          : activeDelta.diff_pp < 0
                          ? 'text-[#C53030]'
                          : 'text-[#687077]')
                      : 'text-[#8C7A5B]'
                  }`}
                >
                  {activeDelta.text}
                </div>
              )}
              <div className="text-[9px] text-[#20231F] font-medium mt-0.5">
                {t('rep.geogrid_ui.in_top_3', { val: top3Count !== null ? (totalRequested !== null ? (isEn ? `${top3Count} of ${totalRequested}` : `${top3Count} из ${totalRequested}`) : top3Count) : (isEn ? 'not measured' : 'не измерено') })}
              </div>
              <div className="text-[9px] text-[#687077]">
                {t('rep.geogrid_ui.found_in_top_20', { val: foundCount !== null ? (totalRequested !== null ? (isEn ? `${foundCount} of ${totalRequested}` : `${foundCount} из ${totalRequested}`) : `${foundCount}`) : (isEn ? 'not measured' : 'не измерено') })}
              </div>
            </div>
            <div className="p-2 rounded bg-white border border-[#E5E1D6]">
              <div className="text-[10px] text-[#687077] uppercase font-bold tracking-wider">
                {t('rep.geogrid_ui.top10_share')}
              </div>
              <div className="text-base font-bold text-[#20231F] mt-0.5">{top10Pct}</div>
              <div className="text-[9px] text-[#20231F] font-medium mt-0.5">
                {t('rep.geogrid_ui.in_top_10', { val: top10Count !== null ? (totalRequested !== null ? (isEn ? `${top10Count} of ${totalRequested}` : `${top10Count} из ${totalRequested}`) : top10Count) : (isEn ? 'not measured' : 'не измерено') })}
              </div>
              <div className="text-[9px] text-[#687077]">
                {radiusKm !== null ? t('rep.geogrid_ui.radius_reach', { radius: radiusKm }) : t('rep.geogrid_ui.radius_unmeasured')}
              </div>
            </div>
          </div>

          {/* Покрытие и ворота качества: строго из метрик */}
          <div className="p-2 rounded bg-white border border-[#E5E1D6]">
            <div className="text-[10px] text-[#687077] flex items-center justify-between">
              <span>{t('rep.geogrid_ui.scan_coverage')}</span>
              <span className="font-semibold text-[#20231F]">
                {coveragePercent !== null
                  ? `${coveragePercent}% (${typeof metrics?.validObservationsCount === 'number' ? metrics.validObservationsCount : (typeof metrics?.foundCount === 'number' ? metrics.foundCount : '—')}/${totalRequested !== null ? totalRequested : '—'} ${isEn ? 'points' : 'точек'})`
                  : (isEn ? 'not measured' : 'не измерено')}
              </span>
            </div>

            {metrics && metrics.meetsQualityGate === false && (
              <div className="mt-1.5 p-1.5 rounded bg-[#FFF5F5] border border-[#FED7D7] text-[10px] text-[#C53030] flex items-center gap-1.5 font-medium">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{t('rep.geogrid_ui.quality_gate_failed', { coverage: coveragePercent !== null ? `${coveragePercent}%` : (isEn ? 'not measured' : 'не измерено') })}</span>
              </div>
            )}
          </div>

          {/* Топ-конкуренты по запросу */}
          <div className="p-2.5 rounded bg-white border border-[#E5E1D6]">
            <div className="text-[10px] font-bold text-[#20231F] flex items-center gap-1 mb-1.5">
              <Users className="w-3 h-3 text-[#C8A653]" />
              <span>{t('rep.geogrid_ui.search_leaders')}</span>
            </div>

            {/* Отдельная строка для нашей фирмы */}
            <div className="flex items-center justify-between text-[10px] text-[#20231F] pb-1.5 mb-1.5 border-b border-[#F1EEE4]">
              <span className="font-semibold">
                {(() => {
                  const firmName = payload?.run?.displayName?.split('—')?.[0]?.trim() || branding?.company_name;
                  return firmName ? `${firmName} ${isEn ? '(our firm)' : '(наша фирма)'}` : (isEn ? 'Our firm' : 'Наша фирма');
                })()}
              </span>
              <span className="shrink-0 text-[#0B8B5E] font-bold text-[9px]">
                {top3Count !== null ? `${top3Count}× ${isEn ? 'in top-3' : 'в топ-3'}` : (isEn ? 'not measured' : 'не измерено')}
              </span>
            </div>

            {topCompetitors.length > 0 ? (
              <div className="space-y-1">
                {topCompetitors.map((comp, idx) => (
                  <div
                    key={comp.title + idx}
                    className="flex items-center justify-between text-[10px] text-[#20231F]"
                  >
                    <div className="truncate pr-2 font-medium flex items-center gap-1">
                      <span className="truncate">{comp.title}</span>
                      {comp.isOtherOwnOffice && (
                        <span className="text-[9px] text-[#A97F2E] font-medium bg-[#FAF4E5] border border-[#E5D7B5] px-1 rounded shrink-0">
                          {isEn ? '(our office)' : '(наш офис)'}
                        </span>
                      )}
                      {typeof comp.rating === 'number' && typeof comp.reviewsCount === 'number' && (
                        <span className="text-[9px] text-[#687077] shrink-0 font-normal">
                          ★ {comp.rating.toFixed(1)} ({comp.reviewsCount})
                        </span>
                      )}
                    </div>
                    <span className="shrink-0 text-[#687077] font-semibold text-[9px]">
                      {comp.top3Count}× {isEn ? 'in top-3' : 'в топ-3'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[10px] text-[#687077]">
                {t('rep.geogrid_ui.competitors_not_found')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Обязательный блок: Что эти числа не доказывают */}
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowDisclaimer(!showDisclaimer)}
          className="flex items-center justify-between w-full text-[10px] font-semibold text-[#8C7A5B] hover:text-[#20231F] transition-colors py-1"
        >
          <span className="flex items-center gap-1">
            <HelpCircle className="w-3 h-3" />
            <span>{t('rep.geogrid_ui.disclaimer_title')}</span>
          </span>
          {showDisclaimer ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
        </button>

        {showDisclaimer && (
          <div className="mt-1.5 p-2 rounded bg-white border border-[#E5D7B5] text-[10px] text-[#687077] space-y-1 leading-relaxed">
            <div>
              1. <strong>{t('rep.geogrid_ui.disclaimer_1_title')}</strong> {t('rep.geogrid_ui.disclaimer_1_text')}
            </div>
            <div>
              2. <strong>{t('rep.geogrid_ui.disclaimer_2_title')}</strong> {t('rep.geogrid_ui.disclaimer_2_text')}
            </div>
            <div>
              3. <strong>{t('rep.geogrid_ui.disclaimer_3_title')}</strong> {t('rep.geogrid_ui.disclaimer_3_text')}
            </div>
          </div>
        )}
      </div>

      {/* Кнопки действий офиса */}
      {actionButtons && (
        <div className="pt-3 border-t border-[#E5E1D6] flex flex-wrap items-center gap-2">
          {actionButtons}
        </div>
      )}

      <GeogridKeywordsModal
        isOpen={isKeywordsModalOpen}
        onClose={() => setIsKeywordsModalOpen(false)}
        onSaved={handleKeywordsSaved}
        kluch={kluch}
        officeName={payload?.displayName || kluch}
      />
    </div>
  );
};

export default OfficeVisibilityBlock;
